/* Copyright (c) 2020-present CloudIO Inc */

import {
  baseFieldProps,
  fontStyleProps,
} from 'componentregistry/components/TextField/BaseField';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { defaultValue } from './NumberField';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { analyzeNumberField } from './NumberFieldAnalyze';
import { isEmpty } from 'utils/isEmpty';

const NumberFieldManager: ComponentManagerFn<'NumberField'> = (
  platform,
): ComponentManager<'NumberField'> => {
  return {
    ...baseComponentManager<'NumberField'>(platform, 'NumberField'),
    type: 'NumberField',
    name: 'Number Field',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'EndIcon',
          'PulseTip',
          'StartIcon',
          'TriggerOnBlur',
          'TriggerOnChange',
          'TriggerOnFocus',
          'TriggerOnValueChange',
          'TriggerOnEnterKeyPress',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'prefix':
        case 'suffix':
        case 'thousandSeparator':
        case 'allowDecimal':
          return isEmpty(itemData.format);
        case 'fixedDecimalScale':
          return isEmpty(itemData.format) && itemData.allowDecimal === true;
        case 'decimalScale':
          return (
            isEmpty(itemData.format) &&
            itemData.fixedDecimalScale === true &&
            itemData.allowDecimal === true
          );
        case 'sliderMin':
        case 'sliderMax':
        case 'sliderStep':
          return itemData.useSlider === true;
        case 'step':
        case 'hideSpinner':
          return (
            itemData.useSlider === false &&
            !itemData.fixedDecimalScale &&
            !itemData.thousandSeparator &&
            isEmpty(itemData.format) &&
            isEmpty(itemData.prefix) &&
            isEmpty(itemData.suffix)
          );
        case 'skipScrollOnChange':
          return (
            itemData.useSlider === false &&
            !itemData.hideSpinner &&
            !itemData.fixedDecimalScale &&
            !itemData.thousandSeparator &&
            isEmpty(itemData.format) &&
            isEmpty(itemData.prefix) &&
            isEmpty(itemData.suffix)
          );
        default:
          return true;
      }
    },
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => {
          return ['Integer', 'Decimal', 'Double', 'Percent'];
        },
      },
      minValue: {
        label: 'Min Value',
        type: 'numberInput',
        category: 'Validation',
      },
      maxValue: {
        label: 'Max Value',
        type: 'numberInput',
        category: 'Validation',
      },
      allowNegative: {
        label: 'Allow Negative',
        type: 'boolean',
        category: 'Validation',
      },
      onChangeFunction: {
        label: 'On Change',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onKeyDownFunction: {
        label: 'On Key Down',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onFocusFunction: {
        label: 'On Focus',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onBlurFunction: {
        label: 'On Blur',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      useSlider: {
        label: 'Use Slider',
        type: 'boolean',
        category: 'Validation',
      },
      sliderMin: {
        label: 'Slider Min Value',
        type: 'numberInput',
        category: 'Validation',
      },
      sliderMax: {
        label: 'Slider Max Value',
        type: 'numberInput',
        category: 'Validation',
      },
      sliderStep: {
        label: 'Slider Step Value',
        type: 'numberInput',
        category: 'Validation',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
      format: {
        label: 'Pattern Format',
        type: 'string',
        category: 'Display',
        helpText: 'e.g. #### #### #### #### or ##/## or (###) ###-####',
      },
      thousandSeparator: {
        label: 'Thousand Separator',
        type: 'boolean',
        category: 'Display',
      },
      allowDecimal: {
        label: 'Allow Decimal',
        type: 'boolean',
        category: 'Display',
      },
      fixedDecimalScale: {
        label: 'Fixed Decimal Scale',
        type: 'boolean',
        category: 'Display',
        helpText: 'If checked it add 0s to match given decimalScale',
      },
      decimalScale: {
        label: 'Decimal Scale',
        type: 'numberInput',
        category: 'Display',
        helpText: 'If defined it limits to given decimal scale',
      },
      suffix: {
        label: 'Suffix',
        type: 'string',
        category: 'Display',
        helpText: 'Suffix character',
      },
      prefix: {
        label: 'Prefix',
        type: 'string',
        category: 'Display',
        helpText: 'prefix character',
      },
      step: {
        category: 'General',
        label: 'Step',
        type: 'string',
      },
      hideSpinner: {
        category: 'General',
        label: 'Hide Spinner',
        type: 'boolean',
      },
      skipScrollOnChange: {
        category: 'General',
        label: 'Skip Value Change On Scroll',
        type: 'boolean',
        helpText:
          'Check this to skip the value change on scroll in  number field',
      },
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    analyze: analyzeNumberField,
    getDisplayText: (item) =>
      (typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value) ?? '',
    category: 'Forms',
    excludeChildren: true,
    icon: { iconName: 'io-number-input', iconType: 'cloudio' },
    replaceableWith: [
      'DateField',
      'LOVField',
      'LookupSelect',
      'Select',
      'TextField',
    ],
    onReplace: (item) => {
      delete item.props.viewAttribute;
      return item;
    },
    description: `**Overview**<br/>
The Number Field is a specialized input control designed exclusively for numerical data entry. Unlike a standard text field, it enforces input validation to ensure only valid numbers are entered and offers extensive formatting options for currencies, percentages, and patterns.
Use this component for any data that requires mathematical precision, such as prices, quantities, ages, or telephone numbers.

**1. Data Binding**<br/>
This component binds to numeric columns in your Data Store.
* **View Attribute:** Select the Data Store field to read from and write to.
* **Supported Types:** **Integer**, **Decimal**, **Double**, **Percent**.

**2. Formatting & Display**<br/>
The Number Field offers two distinct ways to format values. Note that using a **Pattern Format** will hide standard formatting options.
* **A. Standard Numeric Formatting:**
    * **Thousand Separator:** Automatically adds commas (e.g., 1,000,000).
    * **Decimal Scale:** Limits the number of digits allowed after the decimal point.
    * **Fixed Decimal Scale:** If enabled, forces the number to always show the defined decimal places (e.g., converts 5 to 5.00).
    * **Prefix / Suffix:** Adds static text before or after the value (e.g., Prefix: "$", Suffix: "kg").
* **B. Pattern Format:**
    * **Format:** Allows you to define a specific character mask. (Examples: \`#### #### #### ####\` for Credit Cards or \`(###) ###-####\` for Phone Numbers).

**3. Validation & Constraints**<br/>
Ensure data quality by setting strict limits on user input.
* **Min / Max Value:** Defines the acceptable range. If a user enters a number outside this range, the field indicates an error.
* **Allow Negative:** A boolean toggle. If unchecked, the field will prevent the entry of the minus (-) sign.
* **Step:** Defines the increment value when using the up/down spinner arrows (e.g., increment by 0.5 or 10).

**4. Slider Mode**<br/>
For inputs where an approximate value or a visual range is preferred over typing, you can convert the input into a slider.
* **Use Slider:** Switches the UI from a text box to a drag-handle slider.
* **Slider Configuration:** Requires setting **Slider Min**, **Slider Max**, and **Slider Step** to define the boundaries and granularity of the drag action.

**5. Usability Features**<br/>
* **Hide Spinner:** Removes the browser-default up/down arrows from the right side of the input field.
* **Skip Scroll On Change:** Disables value changes via the mouse wheel, preventing accidental data modifications while scrolling the page.
* **Triggers:** Supports specialized event triggers including **TriggerOnEnterKeyPress** and **TriggerOnValueChange**.

**6. Best Use Cases**<br/>
* **Financial Forms:** Capturing currency amounts using Prefix: "$" and Fixed Decimal Scale: 2.
* **Inventory:** Entering stock counts using Integer binding and Min Value: 0.
* **User Settings:** Setting volume or brightness levels using **Slider Mode**.
* **Formatted ID Entry:** Capturing Social Security Numbers or Phone Numbers using the **Format** pattern.`,
    helpRenders: [
      {
        title: 'Default Number Field',
        description: 'With Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        visibleProps: [],
        item: {
          itemId: 'tf',
          props: {
            viewAttribute: 'someNumber',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'NumberField',
          parent: 'form',
        },
      },
      {
        title: 'Number Field w/ Label',
        description: 'With Label',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['label'],
        item: {
          itemId: 'tf1',
          props: {
            label: { type: 'text', value: 'Quantity' },
            viewAttribute: 'someNumber',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'NumberField',
          parent: 'form',
        },
      },
      {
        title: 'Number Field w/ Placeholder',
        description:
          'With Placeholder - Click on the field to see the placeholder',
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['placeholder'],
        item: {
          itemId: 'tf2',
          props: {
            label: { type: 'text', value: 'Quantity' },
            placeholder: 'How much quantity?',
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
          },
          children: [],
          type: 'NumberField',
          parent: 'form',
        },
      },
      {
        title: 'Number Field w/ Min/Max Values',
        description:
          'Try entering a number outside the range to see the field turn red with an error message displayed on mouse hover...',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['minValue', 'maxValue'],
        item: {
          itemId: 'tf3',
          props: {
            label: { type: 'text', value: 'Quantity' },
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
            minValue: 100,
            maxValue: 200,
          },
          children: [],
          type: 'NumberField',
          parent: 'form',
        },
      },
      {
        title: 'Slider',
        description: "Turn on 'Use Slider' prop to render a slider.",
        page: SamplePage,
        exampleId: 'e5',
        height: 260,
        visibleProps: ['useSlider', 'sliderStep', 'sliderMin', 'sliderMax'],
        item: {
          itemId: 'tf4',
          props: {
            label: { type: 'text', value: 'Quantity' },
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
            useSlider: true,
            sliderStep: 10,
          },
          children: [],
          type: 'NumberField',
          parent: 'form',
        },
      },
      {
        title: 'Readonly',
        description:
          'Turning off the insertable prop will disable the control while entering a new row. Updatable prop will disable editing an existing row.',
        page: SamplePage,
        exampleId: 'e6',
        height: 260,
        visibleProps: ['useSlider', 'insertable'],
        item: {
          itemId: 'tf4',
          props: {
            label: { type: 'text', value: 'Quantity' },
            viewAttribute: 'userName',
            flex: {
              all: 12,
            },
            insertable: { type: 'boolean', value: false },
            useSlider: true,
            sliderStep: 10,
          },
          children: [],
          type: 'NumberField',
          parent: 'form',
        },
      },
    ],
  };
};

export default NumberFieldManager;
